/**
 * Normalizes a URL for consistent string matching.
 * Removes query parameters and decodes URI components.
 */
export const normalizeUrl = (u) => {
    if (!u || typeof u !== 'string') return '';
    try {
        const clean = decodeURIComponent(u.split('?')[0]);
        return clean;
    } catch (e) {
        return u.split('?')[0];
    }
};

/**
 * Resolves a human-readable name for an asset based on its photo URL.
 * Scans metadata, pending assets, and the location hierarchy.
 * @param {string} url - The photo URL to resolve.
 * @param {Object} data - The data object containing hierarchy, metadata, etc.
 * @returns {string|null} - The resolved name or null if not found.
 */
export const getAssetNameFromUrl = (url, data = {}) => {
    if (!url) return null;
    const cleanUrl = normalizeUrl(url);

    // 1. Check Global Hierarchy FIRST (The "Source of Truth")
    if (data.hierarchy) {
        // 1a. Flat Array structure (Direct match)
        if (Array.isArray(data.hierarchy)) {
            const asset = data.hierarchy.find(item =>
                typeof item === 'object' && item.url &&
                (item.url === url || normalizeUrl(item.url) === cleanUrl)
            );
            if (asset?.label) return asset.label;
        }

        // 1b. Optimized Tree Search (based on current permit context)
        if (data.hall && data.line && data.subline && !Array.isArray(data.hierarchy)) {
            const instances = data.hierarchy[data.hall]?.[data.line]?.[data.subline]?._instances;
            if (Array.isArray(instances)) {
                for (const i of instances) {
                    // Match main photo
                    if (i.photoUrl && (i.photoUrl === url || normalizeUrl(i.photoUrl) === cleanUrl)) {
                        return i.name;
                    }
                    // Match LOTO photos
                    if (Array.isArray(i.lotoPhotos)) {
                        const loto = i.lotoPhotos.find(lp => lp.url === url || normalizeUrl(lp.url) === cleanUrl);
                        if (loto) return loto.name;
                    }
                }
            }
        }

        // 1c. DEEP SEARCH FALLBACK (Scanning entire tree)
        if (!Array.isArray(data.hierarchy)) {
            try {
                for (const hallKey in data.hierarchy) {
                    const hall = data.hierarchy[hallKey];
                    if (!hall || typeof hall !== 'object') continue;

                    for (const lineKey in hall) {
                        const line = hall[lineKey];
                        if (!line || typeof line !== 'object') continue;

                        for (const sublineKey in line) {
                            const subline = line[sublineKey];
                            const instances = subline?._instances || [];

                            if (Array.isArray(instances)) {
                                for (const i of instances) {
                                    // Match main photo
                                    if (i.photoUrl && (i.photoUrl === url || normalizeUrl(i.photoUrl) === cleanUrl)) {
                                        return i.name;
                                    }
                                    // Match LOTO photos
                                    if (Array.isArray(i.lotoPhotos)) {
                                        const loto = i.lotoPhotos.find(lp => lp.url === url || normalizeUrl(lp.url) === cleanUrl);
                                        if (loto) return loto.name;
                                    }
                                }
                            }
                        }
                    }
                }
            } catch (err) {
                // Ignore search errors
            }
        }
    }

    // 2. Check Pending Assets (Wizard Context)
    if (Array.isArray(data.pendingAssets)) {
        for (const a of data.pendingAssets) {
            if (!a.instanceData) continue;
            // Match main photo
            if (a.instanceData.photoUrl === url || normalizeUrl(a.instanceData.photoUrl) === cleanUrl) {
                return a.instanceData.name;
            }
            // Match LOTO photos (newly promoted data during Wizard)
            if (Array.isArray(a.instanceData.lotoPhotos)) {
                const loto = a.instanceData.lotoPhotos.find(lp => lp.url === url || normalizeUrl(lp.url) === cleanUrl);
                if (loto) return loto.name;
            }
        }
    }

    // 3. Check Image Metadata (Permit Local Fallback)
    if (data.imageMetadata?.[url]?.name) return data.imageMetadata[url].name;
    if (data.imageMetadata?.[cleanUrl]?.name) return data.imageMetadata[cleanUrl].name;

    // 4. Check LOTO Metadata (Permit Local Fallback)
    if (data.lotoMetadata?.[url]?.tagName) return data.lotoMetadata[url].tagName;
    if (data.lotoMetadata?.[cleanUrl]?.tagName) return data.lotoMetadata[cleanUrl].tagName;

    return null;
};
